/**
 * ReportAd - Premium Report Violation Page with Robust Submission
 * 
 * Features:
 * - FormData submit with optional image upload
 * - 2MB JPG/PNG validation (client-side)
 * - Retry mechanism for failed submissions
 * - Disable button during submission
 * - Safe back navigation (prevents history loops)
 * - Full RTL support for Arabic
 * - Accountability warning
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertTriangle, 
  Loader2, 
  CheckCircle, 
  Camera, 
  AlertCircle, 
  X, 
  ChevronDown,
  ChevronUp,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import './ReportAd.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Full list of report reasons - first 6 visible, rest in "More"
const REPORT_REASONS = [
  // Primary reasons (always visible)
  { id: 'banned_item', labelEn: 'Banned item', labelAr: 'منتج ممنوع' },
  { id: 'scam_fraud', labelEn: 'Scam / Fraud', labelAr: 'احتيال' },
  { id: 'wrong_category', labelEn: 'Wrong category', labelAr: 'تصنيف خاطئ' },
  { id: 'duplicate', labelEn: 'Duplicate ad', labelAr: 'إعلان مكرر' },
  { id: 'misleading', labelEn: 'Misleading information', labelAr: 'معلومات مضللة' },
  { id: 'inappropriate', labelEn: 'Inappropriate content', labelAr: 'محتوى غير لائق' },
  // More reasons (collapsible)
  { id: 'offensive', labelEn: 'Offensive language', labelAr: 'لغة مسيئة' },
  { id: 'fake_price', labelEn: 'Fake price', labelAr: 'سعر وهمي' },
  { id: 'spam', labelEn: 'Spam advertisement', labelAr: 'إعلان مزعج' },
  { id: 'harassment', labelEn: 'Harassment', labelAr: 'تحرش أو مضايقة' },
  { id: 'copyright', labelEn: 'Copyright violation', labelAr: 'انتهاك حقوق النشر' },
  { id: 'other', labelEn: 'Other', labelAr: 'أخرى' },
];

// Custom hook for safe back navigation
function useSafeBack(fallbackPath = '/') {
  const nav = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    // Pop real history first — preserves the origin page's state/scroll.
    if (hasInAppHistory()) {
      nav(-1);
      return;
    }
    const from = location.state?.from;
    if (from) {
      nav(from, { replace: true });
      return;
    }
    nav(fallbackPath, { replace: true });
  }, [nav, location.state, fallbackPath]);
}

function ReportAd() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isArabic = i18n.language?.startsWith('ar');
  
  // Safe back navigation - fallback to ad details page
  const safeBack = useSafeBack(`/ad/${adId}`);

  // Get ad title from location state or use placeholder
  const adTitle = location.state?.adTitle || '';
  
  // Form state
  const [selectedReason, setSelectedReason] = useState(null);
  const [showMoreReasons, setShowMoreReasons] = useState(false);
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const fileInputRef = useRef(null);

  // Check if form has changes (dirty state)
  const isDirty = useMemo(() => {
    return !!selectedReason || description.length > 0 || !!imageFile;
  }, [selectedReason, description, imageFile]);

  // Split reasons into visible and "more" sections
  const visibleReasons = REPORT_REASONS.slice(0, 6);
  const moreReasons = REPORT_REASONS.slice(6);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Scroll to top on mount and route changes
  useEffect(() => {
    // Prevent browser scroll restoration for this page
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Force scroll to top using multiple methods for cross-browser compatibility
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also try scrolling the main content container if it exists
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
  }, [adId, location.key]);

  // Translation helper
  const t = useCallback((ar, en) => (isArabic ? ar : en), [isArabic]);

  // Handle close/back with dirty check
  const handleClose = useCallback(() => {
    if (isDirty && !submitted) {
      const confirmMsg = isArabic
        ? 'لديك اختيار غير محفوظ. هل تريد الخروج؟'
        : 'You have an unfinished selection. Exit?';
      
      if (window.confirm(confirmMsg)) {
        safeBack();
      }
      return;
    }
    safeBack();
  }, [isDirty, submitted, isArabic, safeBack]);

  // Handle file selection with validation
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError(null);

    // Validate file type (JPG/PNG only)
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      const errMsg = isArabic 
        ? 'صيغة غير مدعومة. يرجى اختيار JPG أو PNG فقط' 
        : 'Only JPG/PNG images are allowed';
      setImageError(errMsg);
      toast.error(errMsg);
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      const errMsg = isArabic 
        ? 'حجم الملف كبير جداً (الحد الأقصى 2 ميجابايت)' 
        : 'File too large (max 2MB)';
      setImageError(errMsg);
      toast.error(errMsg);
      return;
    }

    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    toast.success(isArabic ? 'تم اختيار الصورة' : 'Image selected');
  };

  // Remove selected image
  const removeImage = () => {
    setImageFile(null);
    setImageError(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle reason selection with haptic feedback
  const handleSelectReason = (reasonId) => {
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    setSelectedReason(reasonId);
    setError('');
  };

  // Submit report with retry mechanism
  const handleSubmit = async () => {
    if (!selectedReason) {
      const errMsg = isArabic ? 'يرجى اختيار سبب البلاغ' : 'Please select a reason';
      setError(errMsg);
      toast.error(errMsg);
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      // Build FormData for multipart submission
      const formData = new FormData();
      formData.append('reason', selectedReason);
      
      if (description.trim()) {
        formData.append('description', description.trim());
      }
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/ads/${adId}/report-with-image`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        toast.success(
          isArabic 
            ? 'تم إرسال البلاغ وسيتم مراجعته.' 
            : 'Your report was submitted for review.'
        );
        
        // Navigate back to Ad Details after success (with replace to prevent history loop)
        setTimeout(() => {
          navigate(`/ad/${adId}`, { replace: true, state: { reported: true } });
        }, 2000);
        
      } else if (response.status === 409) {
        // Already reported
        toast.error(isArabic ? 'لقد أبلغت عن هذا الإعلان مسبقاً' : 'You have already reported this ad');
        setTimeout(() => {
          navigate(`/ad/${adId}`, { replace: true });
        }, 1500);
        
      } else if (response.status === 429) {
        // Rate limited
        const message = data.detail?.message || (isArabic ? 'يرجى الانتظار قبل الإبلاغ مرة أخرى' : 'Please wait before reporting again');
        setError(message);
        toast.error(message);
        
      } else {
        // Other error - trigger retry mechanism
        throw new Error(data.detail?.message || data.message || 'Submission failed');
      }
      
    } catch (err) {
      console.error('Report submission error:', err);
      
      // Retry mechanism (max 2 retries)
      if (retryCount < 2) {
        setRetryCount(prev => prev + 1);
        const retryMsg = isArabic 
          ? `فشل الإرسال، جاري المحاولة مرة أخرى... (${retryCount + 1}/2)` 
          : `Submission failed, retrying... (${retryCount + 1}/2)`;
        toast.info(retryMsg);
        
        // Wait 1 second and retry
        setTimeout(() => {
          handleSubmit();
        }, 1000);
      } else {
        const errMsg = isArabic 
          ? 'حدث خطأ، يرجى المحاولة لاحقاً' 
          : 'An error occurred, please try again later';
        setError(errMsg);
        toast.error(errMsg);
        setRetryCount(0); // Reset retry count
      }
    } finally {
      if (retryCount >= 2 || submitted) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="report-ad-page" dir={isArabic ? 'rtl' : 'ltr'}>
      <AppHeader
        title={t('الإبلاغ عن إعلان', 'Report this Ad')}
        onBack={safeBack}
        testId="report-ad-header"
        rightSlot={
          <button
            type="button"
            onClick={handleClose}
            className="header-icon-btn close-btn"
            aria-label={isArabic ? 'إغلاق' : 'Close'}
            data-testid="report-close-btn"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        }
      />

      {submitted ? (
        /* Success State */
        <div className="report-success-state" data-testid="report-success">
          <div className="success-icon-wrapper">
            <CheckCircle size={56} className="success-icon" />
          </div>
          <p className="success-text">
            {t('تم إرسال البلاغ بنجاح', 'Report submitted successfully')}
          </p>
        </div>
      ) : (
        /* Main Form */
        <div className="report-content">
          {/* Hero Icon */}
          <div className="report-hero-icon" data-testid="report-hero-icon">
            <span className="hero-emoji">⚠️</span>
          </div>

          {/* Question Heading */}
          <h2 className={`report-heading ${isArabic ? 'text-rtl' : ''}`}>
            {t('ما هو سبب الإبلاغ؟', 'What is the reason?')}
            <span className="required-star"> *</span>
          </h2>

          {/* Reason Selection Card */}
          <div className="report-card" data-testid="report-reasons-card">
            {/* Visible Reasons */}
            {visibleReasons.map((reason) => {
              const isActive = selectedReason === reason.id;
              return (
                <button
                  key={reason.id}
                  type="button"
                  onClick={() => handleSelectReason(reason.id)}
                  className={`reason-row ${isActive ? 'active' : ''}`}
                  data-testid={`reason-${reason.id}`}
                >
                  <span className={`reason-text ${isArabic ? 'text-rtl' : ''} ${isActive ? 'active' : ''}`}>
                    {isArabic ? reason.labelAr : reason.labelEn}
                  </span>
                  <span className={`radio-outer ${isActive ? 'active' : ''}`}>
                    {isActive && <span className="radio-inner" />}
                  </span>
                </button>
              );
            })}

            {/* More Reasons Toggle */}
            <button
              type="button"
              onClick={() => setShowMoreReasons(!showMoreReasons)}
              className="more-toggle"
              data-testid="toggle-more-reasons"
            >
              <span>{t('المزيد من الأسباب', 'More reasons')}</span>
              {showMoreReasons ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {/* Collapsible More Reasons */}
            {showMoreReasons && (
              <div className="more-reasons-section" data-testid="more-reasons-section">
                {moreReasons.map((reason) => {
                  const isActive = selectedReason === reason.id;
                  return (
                    <button
                      key={reason.id}
                      type="button"
                      onClick={() => handleSelectReason(reason.id)}
                      className={`reason-row ${isActive ? 'active' : ''}`}
                      data-testid={`reason-${reason.id}`}
                    >
                      <span className={`reason-text ${isArabic ? 'text-rtl' : ''} ${isActive ? 'active' : ''}`}>
                        {isArabic ? reason.labelAr : reason.labelEn}
                      </span>
                      <span className={`radio-outer ${isActive ? 'active' : ''}`}>
                        {isActive && <span className="radio-inner" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Optional Description - Show for "other" reason or always */}
          {selectedReason === 'other' && (
            <div className="report-description-section">
              <label className="report-label">
                {t('تفاصيل إضافية', 'Additional details')}
                <span className="optional-tag">{t('(اختياري)', '(optional)')}</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                placeholder={t('أضف تفاصيل إضافية...', 'Add more details...')}
                className="description-textarea"
                rows={3}
                maxLength={200}
                data-testid="report-description"
              />
              <span className="char-count">{description.length}/200</span>
            </div>
          )}

          {/* Evidence Image Upload - Optional */}
          <div className="report-evidence-section">
            <label className="report-label">
              {t('صورة كدليل', 'Evidence image')}
              <span className="optional-tag">{t('(اختياري)', '(optional)')}</span>
            </label>
            
            {!imageFile ? (
              <button
                type="button"
                className="evidence-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
                data-testid="upload-evidence-btn"
              >
                <Camera size={22} />
                <span>{t('إضافة صورة', 'Add image')}</span>
              </button>
            ) : (
              <div className="evidence-preview">
                <img 
                  src={imagePreview} 
                  alt="Evidence" 
                  className="evidence-thumb"
                  data-testid="evidence-preview"
                />
                <button
                  type="button"
                  className="evidence-remove-btn"
                  onClick={removeImage}
                  aria-label={t('إزالة الصورة', 'Remove image')}
                  data-testid="remove-evidence-btn"
                >
                  <X size={18} />
                </button>
                <div className="evidence-success-badge">
                  <CheckCircle size={14} />
                </div>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="evidence-file-input"
            />
            
            {imageError ? (
              <p className="evidence-error">{imageError}</p>
            ) : (
              <p className="evidence-hint">
                {t('JPG أو PNG، حد أقصى 2 ميجابايت', 'JPG or PNG, max 2MB')}
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="report-error-box" data-testid="report-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Accountability Warning Box */}
          <div className="accountability-warning" data-testid="accountability-warning">
            <AlertCircle size={18} className="warning-icon" />
            <div className="warning-content">
              <p>{t(
                'يتم مراجعة البلاغات بعناية. البلاغات الكاذبة أو إساءة استخدام الميزة قد تؤدي إلى تعليق الحساب.',
                'Reports are reviewed carefully. False reports or abuse may lead to account suspension.'
              )}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="report-actions">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedReason || isSubmitting}
              className="report-submit-btn"
              data-testid="report-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>{t('جاري الإرسال...', 'Sending...')}</span>
                </>
              ) : (
                <span>{t('إرسال البلاغ', 'Submit Report')}</span>
              )}
            </button>

            <button
              type="button"
              onClick={safeBack}
              className="report-cancel-btn"
              data-testid="report-cancel"
            >
              {t('إلغاء', 'Cancel')}
            </button>
          </div>

          {/* Bottom spacing */}
          <div className="bottom-spacer" />
        </div>
      )}
    </div>
  );
}

export default ReportAd;
